import { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  alpha,
  styled,
} from '@mui/material';
import type { GridColDef, GridPaginationModel, GridRenderCellParams } from '@mui/x-data-grid';
import { RefreshOutlined, SearchOutlined, ClearOutlined } from '@mui/icons-material';
import { Table } from '../../../components/Table/Table';
import { useGetUsersWithFiltersQuery } from '../../../services/restApi/queries/users.service';
import { type UserVM, UserRole, UserStatus } from '@tto/shared-models';

const FilterBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3),
  flexWrap: 'wrap',
  alignItems: 'center',
}));

const StatusChip = styled(Chip)<{ status: UserStatus }>(({ theme, status }) => {
  const getStatusColor = () => {
    switch (status) {
      case UserStatus.ACTIVE:
        return {
          bg: alpha(theme.palette.success.main, 0.1),
          color: theme.palette.success.dark,
        };
      case UserStatus.INVITED:
        return {
          bg: alpha(theme.palette.info.main, 0.1),
          color: theme.palette.info.dark,
        };
      case UserStatus.DEACTIVATED:
        return {
          bg: alpha(theme.palette.error.main, 0.1),
          color: theme.palette.error.dark,
        };
      default:
        return {
          bg: alpha(theme.palette.grey[500], 0.1),
          color: theme.palette.grey[700],
        };
    }
  };

  const colors = getStatusColor();
  return {
    backgroundColor: colors.bg,
    color: colors.color,
    fontWeight: 600,
  };
});

const RoleChip = styled(Chip)<{ role: UserRole }>(({ theme, role }) => {
  const getRoleColor = () => {
    switch (role) {
      case UserRole.ADMIN:
        return {
          bg: alpha(theme.palette.primary.main, 0.1),
          color: theme.palette.primary.dark,
        };
      case UserRole.DIRECTOR:
        return {
          bg: alpha(theme.palette.secondary.main, 0.1),
          color: theme.palette.secondary.dark,
        };
      case UserRole.USER:
        return {
          bg: alpha(theme.palette.grey[500], 0.1),
          color: theme.palette.grey[700],
        };
      default:
        return {
          bg: alpha(theme.palette.grey[500], 0.1),
          color: theme.palette.grey[700],
        };
    }
  };

  const colors = getRoleColor();
  return {
    backgroundColor: colors.bg,
    color: colors.color,
    fontWeight: 600,
  };
});

interface UserListProps {
  onUserClick?: (user: UserVM) => void;
}

export const UserList = ({ onUserClick }: UserListProps) => {
  // Pagination
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 20,
  });

  // Filters
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<UserRole | ''>('');
  const [statusFilter, setStatusFilter] = useState<UserStatus | ''>('');

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [search]);

  // Fetch users
  const { data: response, isLoading, isFetching, refetch, error } = useGetUsersWithFiltersQuery();
  // {
  //TODO: Implement filtering and pagination in the backend
  // page: paginationModel.page + 1, // API uses 1-based pagination
  // limit: paginationModel.pageSize,
  // search: debouncedSearch || undefined,
  // role: roleFilter || undefined,
  // status: statusFilter || undefined,
  // sortBy: "createdAt",
  // sortOrder: "desc",
  // }

  // Client-side filtering until backend supports it
  const filteredUsers = useMemo(() => {
    if (!response) return [];

    return response.filter((user) => {
      // Search filter
      if (debouncedSearch) {
        const searchLower = debouncedSearch.toLowerCase();
        const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
        const email = user.email?.toLowerCase() || '';

        if (!fullName.includes(searchLower) && !email.includes(searchLower)) {
          return false;
        }
      }

      // Role filter (case-insensitive comparison since backend stores lowercase)
      if (roleFilter && user.role?.toUpperCase() !== roleFilter.toUpperCase()) {
        return false;
      }

      // Status filter
      //   if (statusFilter && user.status !== statusFilter) {
      //     return false;
      //   }

      return true;
    });
  }, [response, debouncedSearch, roleFilter, statusFilter]);

  // Reset to page 0 when filters change
  useEffect(() => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [debouncedSearch, roleFilter, statusFilter]);

  // Clear all filters
  const handleClearFilters = useCallback(() => {
    setSearch('');
    setRoleFilter('');
    setStatusFilter('');
    setDebouncedSearch('');
  }, []);

  // Check if any filters are active
  const hasActiveFilters = search || roleFilter || statusFilter;

  // DataGrid columns
  const columns: GridColDef<UserVM>[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams<UserVM>) => (
        <Box>
          <Typography variant='body2' fontWeight={600}>
            {params.row.firstName} {params.row.lastName}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'role',
      headerName: 'Role',
      width: 130,
      renderCell: (params: GridRenderCellParams<UserVM>) => (
        <RoleChip role={params.row.role as UserRole} label={params.row.role.replace('_', ' ')} size='small' />
      ),
    },
    // Also commented out until we have status back in the backend
    // {
    //   field: 'status',
    //   headerName: 'Status',
    //   width: 120,
    //   renderCell: (params: GridRenderCellParams<UserVM>) => (
    //     <StatusChip
    //       status={(params.row.status as UserStatus) ?? UserStatus.ACTIVE}
    //       label={(params.row.status as UserStatus) ?? UserStatus.ACTIVE}
    //       size='small'
    //     />
    //   ),
    // },
    // {
    // 	field: "createdAt",
    // 	headerName: "Created",
    // 	width: 150,
    // 	renderCell: (params: GridRenderCellParams<UserVM>) =>
    // 		params.row.createdAt
    // 			? new Date(params.row.createdAt ?? "").toLocaleDateString()
    // 			: "",
    // },
  ];

  return (
    <>
      {/* Filter Bar */}
      <FilterBar>
        {/* Search */}
        <TextField
          size='small'
          placeholder='Search by name or email'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: <SearchOutlined sx={{ mr: 1, color: 'text.secondary' }} />,
            endAdornment: search && (
              <IconButton size='small' onClick={() => setSearch('')}>
                <ClearOutlined fontSize='small' />
              </IconButton>
            ),
          }}
          sx={{ minWidth: 300, flex: 1 }}
        />

        {/* Status Filter */}
        {/* Removed Status filter until we add it on the backend */}
        {/* <FormControl size='small' sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as UserStatus | '')}
            label='Status'
          >
            <MenuItem value=''>All</MenuItem>
            <MenuItem value={UserStatus.ACTIVE}>Active</MenuItem>
            <MenuItem value={UserStatus.INVITED}>Invited</MenuItem>
            <MenuItem value={UserStatus.DEACTIVATED}>Deactivated</MenuItem>
          </Select>
        </FormControl> */}

        {/* Role Filter */}
        <FormControl size='small' sx={{ minWidth: 150 }}>
          <InputLabel>Role</InputLabel>
          <Select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value as UserRole | '')} label='Role'>
            <MenuItem value=''>All</MenuItem>
            <MenuItem value={UserRole.ADMIN}>Admin</MenuItem>
            <MenuItem value={UserRole.DIRECTOR}>Director</MenuItem>
            <MenuItem value={UserRole.USER}>User</MenuItem>
          </Select>
        </FormControl>

        {/* Clear Filters */}
        {hasActiveFilters && (
          <Tooltip title='Clear all filters'>
            <IconButton onClick={handleClearFilters} color='primary'>
              <ClearOutlined />
            </IconButton>
          </Tooltip>
        )}

        {/* Refresh */}
        <Tooltip title='Refresh'>
          <IconButton onClick={() => refetch()} color='primary'>
            <RefreshOutlined />
          </IconButton>
        </Tooltip>

        <Box flex={1} />

        {/* User Count */}
        {response && (
          <Typography variant='body2' color='text.secondary'>
            {hasActiveFilters && `Showing ${filteredUsers.length} of ${response.length} `}
            {!hasActiveFilters && `${response.length} `}
            {(hasActiveFilters ? filteredUsers.length : response.length) === 1 ? 'user' : 'users'}
          </Typography>
        )}
      </FilterBar>

      {/* Data Table */}
      <Table
        rows={filteredUsers}
        columns={columns}
        pageSizeOptions={[10, 20, 50, 100]}
        loading={isLoading}
        refetch={refetch}
        isFetching={isFetching}
        checkboxSelection={false}
        paginationModel={paginationModel}
        setPaginationModel={setPaginationModel}
        //TODO: Enable server-side pagination when supported by backend
        paginationMode='client'
        totalRows={filteredUsers.length}
        onRowClick={(params) => onUserClick?.(params.row)}
        error={!!error}
        sx={{
          '& .MuiDataGrid-root': { border: 'none' },
          '& .MuiDataGrid-columnHeaders': {
            background: (theme) => alpha(theme.colors.primary.main, 0.3),
            fontWeight: 600,
            borderRadius: 0,
          },
          '& .MuiDataGrid-row': {
            transition: (theme) => theme.transitions.create(['background-color', 'transform']),
            '&:hover': { cursor: 'pointer' },
          },
        }}
      />
    </>
  );
};
